import { defineStore } from "pinia";
import accountService from "@/services/account.service";
import type { FavouriteHotelItem } from "@/services/account.service";

const STORAGE_KEY = "favouriteHotelIds";

// There is no get-favourites endpoint yet, so favourite state can only be
// seeded from the hotel-details response (HotelDetails.isFavourite) or from
// what the user toggles this session - persisted locally so it survives a
// refresh. This can go stale across devices until a get-favourites endpoint
// exists.
function loadFromStorage(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function saveToStorage(ids: Set<string>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
}

export interface FavouritesPagination {
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
}

export const useFavouritesStore = defineStore("favourites", {
  state: () => ({
    favouriteIds: loadFromStorage() as Set<string>,
    pendingIds: new Set<string>(),
    error: "",
    items: [] as FavouriteHotelItem[],
    pagination: null as FavouritesPagination | null,
    isLoadingList: false,
    listError: "",
  }),

  getters: {
    isFavourite: (state) => (hotelId: string) =>
      state.favouriteIds.has(hotelId),
    isPending: (state) => (hotelId: string) => state.pendingIds.has(hotelId),
  },

  actions: {
    setFavourite(hotelId: string, value: boolean) {
      if (value) {
        this.favouriteIds.add(hotelId);
      } else {
        this.favouriteIds.delete(hotelId);
      }
      saveToStorage(this.favouriteIds);
    },

    async toggleFavourite(hotelId: string) {
      if (this.pendingIds.has(hotelId)) return;

      const wasFavourite = this.favouriteIds.has(hotelId);
      this.pendingIds.add(hotelId);
      this.error = "";

      // Optimistic update, rolled back on failure below.
      this.setFavourite(hotelId, !wasFavourite);

      try {
        if (wasFavourite) {
          await accountService.removeFavourite(hotelId);
          this.items = this.items.filter((item) => item.id !== hotelId);
        } else {
          await accountService.addFavourite(hotelId);
        }
      } catch (error: any) {
        this.setFavourite(hotelId, wasFavourite);
        this.error =
          error?.message || "Unable to update favourites. Please try again.";
        throw error;
      } finally {
        this.pendingIds.delete(hotelId);
      }
    },

    async fetchFavourites(pageNumber = 1, pageSize = 10) {
      this.isLoadingList = true;
      this.listError = "";

      try {
        const response = await accountService.getFavourites(
          pageNumber,
          pageSize
        );
        if (response && response.data) {
          this.items = response.data.items || [];
          this.pagination = response.data.pagination || null;
          // Merge this page's ids into the toggle-state cache rather than
          // replacing it outright, since favourites on other pages would
          // otherwise be wiped from the cache.
          this.items.forEach((item) => this.favouriteIds.add(item.id));
          saveToStorage(this.favouriteIds);
        }
      } catch (error: any) {
        this.listError =
          error?.message || "Unable to load your favourites. Please try again.";
      } finally {
        this.isLoadingList = false;
      }
    },

    clearFavourites() {
      this.favouriteIds = new Set();
      this.items = [];
      this.pagination = null;
      localStorage.removeItem(STORAGE_KEY);
    },
  },
});

export default useFavouritesStore;
